'use client';

/**
 * AssignmentResults — sección "Reserva por tipo de apartamento" en /reservas
 * (plan, Etapa 4). Carril ADITIVO: se sincroniza con la búsqueda por la URL
 * (router.replace de AvailabilitySearch) y si no hay cotización NO renderiza
 * nada. Flag de UI: SHOW_ASSIGNMENT_RESULTS (el kill switch real es env
 * server-side — ASSIGNMENT_ORDERS_ENABLED apaga el endpoint de quote).
 *
 * F1.5 (PLAN-ACTIVACION-RESERVA-POR-ASIGNACION.md §3.5): kicker de
 * reencuadre honesto ("las unidades con número ya se reservaron, pero aún
 * podemos alojarte") + fusión con el estado vacío de AvailabilitySearch.tsx.
 * Los dos componentes son hermanos (montados por separado en
 * app/reservas/page.tsx, sin padre común que comparta estado), así que la
 * fusión NO puede ser props: AvailabilitySearch decide si esconde su propio
 * mensaje muerto "No hay apartamentos disponibles" haciendo su propia
 * consulta liviana al MISMO endpoint /api/assignment/quote (catálogo en DB +
 * un GET de cupo a Beds24 sobre la room de placeholders — nada de escritura)
 * SOLO cuando su búsqueda nombrada da vacío. El kicker de abajo queda
 * siempre acotado al edificio (`quote.buildingName`) para que sea
 * "literalmente cierto" (principio 1) incluso cuando este carril aparece
 * como upsell debajo de resultados nombrados de OTROS edificios.
 *
 * Fix review F1.5 (Finding 1): este componente NO sabe si la búsqueda
 * nombrada de AvailabilitySearch trajo resultados (son hermanos, sin padre
 * común). Si hay unidades con número del MISMO edificio listadas arriba, el
 * kicker de escasez es una afirmación falsa y autocontradictoria en la misma
 * pantalla. AvailabilitySearch emite un CustomEvent en window
 * ('pacartagena:availability-named-results', mismo patrón que
 * StickyMobileCTA → AvailabilitySearchForm) cada vez que su búsqueda
 * resuelve; mientras no llega señal (o llega "hay nombradas"), este
 * componente usa el encabezado neutro pre-F1.5 — nunca el kicker por
 * defecto.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import AssignmentTypeCard from './assignment-type-card';
import GroupOptionCard from './group-option-card';
import AssignmentPaymentModal, { type AssignmentSelection } from './assignment-payment-modal';
import type { AssignmentQuote, AssignmentTypeCardDTO, GroupOptionDTO } from '@/lib/assignment/quote';
import { trackPostHogEvent, type CTAEventProps } from '@/lib/analytics/posthog';

/**
 * Flag de UI (apagable sin deploy de env): muestra/oculta la sección.
 * Exportado: AvailabilitySearch.tsx lo lee para saber si este hermano puede
 * llegar a renderizar algo antes de esconder su propio mensaje muerto
 * (review F1.5, Finding 3 — evita el "doble vacío" cuando el kill switch
 * está apagado pero el fallback cree que sí hay cotización).
 */
export const SHOW_ASSIGNMENT_RESULTS = true;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export default function AssignmentResults() {
  const searchParams = useSearchParams();
  const arrival = searchParams.get('arrival') ?? '';
  const departure = searchParams.get('departure') ?? '';
  const numAdults = searchParams.get('numAdults') ?? '';
  const guestAdults = searchParams.get('guestAdults') ?? '';
  const childrenAges = searchParams.get('childrenAges') ?? '';
  const building = searchParams.get('building') ?? '';
  const neighborhood = searchParams.get('neighborhood') ?? '';

  // adultos reales + niños (memoria del repo: preservar guestAdults/childrenAges;
  // numAdults legacy = capacidad total)
  const children = childrenAges ? childrenAges.split(',').filter(Boolean).length : 0;
  const adultsRaw = guestAdults || numAdults;
  const adults = /^\d+$/.test(adultsRaw) ? parseInt(adultsRaw, 10) : 0;
  const people = adults + children;

  const [quote, setQuote] = useState<AssignmentQuote | null>(null);
  const [selection, setSelection] = useState<AssignmentSelection | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const requestIdRef = useRef(0);

  // Señal del hermano AvailabilitySearch (Finding 1, review F1.5): true SOLO
  // cuando confirma que su búsqueda nombrada dio vacío. Default false
  // (asumir "puede haber nombradas") — nunca mostrar el kicker por defecto o
  // ante silencio/timing de la señal, solo tras confirmación explícita.
  const [namedResultsEmpty, setNamedResultsEmpty] = useState(false);

  useEffect(() => {
    const onNamedResultsSignal = (event: Event) => {
      const detail = (event as CustomEvent<{ hasNamedResults?: boolean }>).detail;
      if (!detail) return;
      setNamedResultsEmpty(!detail.hasNamedResults);
    };
    window.addEventListener('pacartagena:availability-named-results', onNamedResultsSignal);
    return () =>
      window.removeEventListener('pacartagena:availability-named-results', onNamedResultsSignal);
  }, []);

  const ctaAttribution: CTAEventProps = useMemo(
    () => ({
      source: 'reservas_assignment',
      building_id: building || undefined,
      neighborhood: neighborhood || undefined,
      arrival,
      departure,
    }),
    [building, neighborhood, arrival, departure]
  );

  useEffect(() => {
    if (!SHOW_ASSIGNMENT_RESULTS) return;
    if (!DATE_RE.test(arrival) || !DATE_RE.test(departure) || adults < 1) {
      setQuote(null);
      setNamedResultsEmpty(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    setQuote(null); // limpiar al iniciar otra búsqueda (regla resolvedSearch del repo)
    // Nueva búsqueda: la señal de "nombradas vacío" de la anterior queda
    // obsoleta hasta que AvailabilitySearch confirme la nueva (Finding 1).
    setNamedResultsEmpty(false);

    const params = new URLSearchParams({
      arrival,
      departure,
      adults: String(adults),
      children: String(children),
    });
    if (building) params.set('building', building);
    if (neighborhood) params.set('neighborhood', neighborhood);

    fetch(`/api/assignment/quote?${params.toString()}`, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        if (requestId !== requestIdRef.current) return; // respuesta fuera de orden
        const next: AssignmentQuote | null = data?.success ? data.assignment : null;
        setQuote(next);
        if (next && (next.products.length > 0 || next.groupOptions.length > 0)) {
          trackPostHogEvent('assignment_section_viewed', {
            source: 'reservas_assignment',
            arrival,
            departure,
            people,
            products_count: next.products.length,
            group_options_count: next.groupOptions.length,
            building_id: building || undefined,
            neighborhood: neighborhood || undefined,
          });
        }
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setQuote(null);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrival, departure, adults, children, building, neighborhood]);

  if (!SHOW_ASSIGNMENT_RESULTS || !quote) return null;
  const { products, groupOptions, buildingName } = quote;
  if (products.length === 0 && groupOptions.length === 0) return null;

  const openProductModal = (product: AssignmentTypeCardDTO) => {
    setSelection({
      optionFingerprint: product.optionFingerprint,
      lines: [
        {
          kind: 'assignment',
          ref: product.slug,
          units: 1,
          displayName: product.displayName,
          totalPerUnit: product.totalPerUnit,
        },
      ],
      totalCop: product.totalPerUnit,
      depositCop: product.depositCop,
      nights: product.nights,
      arrival,
      departure,
      adults,
      children,
      hasAssignmentItems: true,
    });
    setModalOpen(true);
  };

  const openGroupModal = (option: GroupOptionDTO) => {
    setSelection({
      optionFingerprint: option.optionFingerprint,
      lines: option.lines.map((l) => ({
        kind: l.kind,
        ref: l.kind === 'assignment' ? (l.productSlug ?? '') : (l.apartmentId ?? ''),
        units: l.units,
        displayName: l.displayName,
        totalPerUnit: l.totalPerUnit,
      })),
      totalCop: option.totalCop,
      depositCop: option.depositCop,
      nights: option.nights,
      arrival,
      departure,
      adults,
      children,
      hasAssignmentItems: option.lines.some((l) => l.kind === 'assignment'),
    });
    setModalOpen(true);
  };

  return (
    <section aria-labelledby="assignment-section-title" className="mt-10">
      {/* ── Encabezado de sección ──
          Reencuadre de escasez honesta (principio 1) + modelo mental de
          hotel (principio 2). Camino "grupo" (combos) queda inerte en v1
          (ASSIGNMENT_V1_SINGLE_UNIT) pero se conserva el copy por si se
          reactiva — código dormido, no se borra.
          El kicker de escasez ("ya se reservaron") SOLO sale cuando
          namedResultsEmpty confirma que la búsqueda nombrada del hermano dio
          vacío (Finding 1, review F1.5) — de lo contrario cae al encabezado
          neutro pre-F1.5, porque el kicker sería una afirmación falsa con
          unidades nombradas listadas arriba en la misma pantalla. */}
      {groupOptions.length > 0 ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <h3 id="assignment-section-title" className="text-2xl font-bold text-gray-900">
              Opciones para tu grupo de {people} personas
            </h3>
            <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-800">
              Precio fijo por temporada
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-gray-600">
            Te confirmamos el piso y el número de apartamento antes del check-in, según
            disponibilidad. Todas las unidades de este tipo están en el mismo edificio, con el
            mismo lobby, piscina y ubicación frente al mar.
          </p>
        </>
      ) : namedResultsEmpty ? (
        <div className="max-w-3xl">
          <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-800">
            Precio fijo por temporada
          </span>
          <h3
            id="assignment-section-title"
            className="mt-3 text-2xl font-bold leading-snug text-gray-900"
          >
            Los apartamentos con número del {buildingName} ya se reservaron para tus fechas
            — pero aún podemos alojarte
          </h3>
          <p className="mt-2 text-gray-600">
            Como en un hotel: reservas la categoría y nosotros te garantizamos la unidad —
            misma capacidad, mismo edificio, mismo frente al mar.
          </p>
        </div>
      ) : (
        <div className="max-w-3xl">
          <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-800">
            Precio fijo por temporada
          </span>
          <h3
            id="assignment-section-title"
            className="mt-3 text-2xl font-bold leading-snug text-gray-900"
          >
            Reserva por tipo de apartamento
          </h3>
          <p className="mt-2 text-gray-600">
            Tu reserva queda confirmada al instante; el número exacto de tu unidad te lo
            confirmamos antes del check-in. Todas las unidades de este tipo están en el mismo
            edificio, con el mismo lobby, piscina y ubicación frente al mar.
          </p>
        </div>
      )}

      {/* ── Cards de producto (búsqueda individual, máx 4, precio asc) ── */}
      {products.length > 0 && (
        <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((product) => (
            <AssignmentTypeCard
              key={product.slug}
              product={product}
              arrival={arrival}
              departure={departure}
              people={people}
              ctaAttribution={ctaAttribution}
              onReserve={openProductModal}
            />
          ))}
        </div>
      )}

      {/* ── Opciones de grupo (cascada, máx 3, apiladas) ── */}
      {groupOptions.length > 0 && (
        <div className="mt-5 space-y-4">
          {groupOptions.map((option) => (
            <GroupOptionCard
              key={option.optionFingerprint}
              option={option}
              arrival={arrival}
              departure={departure}
              ctaAttribution={ctaAttribution}
              onReserve={openGroupModal}
            />
          ))}
        </div>
      )}

      <AssignmentPaymentModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selection={selection}
        ctaAttribution={ctaAttribution}
      />
    </section>
  );
}
